import React, { useEffect, useState } from "react";
import { services } from "../data/services.js";
import { brand } from "../theme/brand.js";
import { ServiceSection } from "./ServiceSection.jsx";

const SECTIONS = [
  {
    section: 2,
    title: "Events",
    description: "Event-based intake tools for naturalization workshops and immigration enforcement response.",
  },
  {
    section: 3,
    title: "Naturalization",
    description: "Tools for preparing and filing a Form N-400 Application for Naturalization.",
  },
  {
    section: 5,
    title: "Asylum",
    description: "Tools for preparing and filing a Form I-589 Application for Asylum and for Withholding of Removal.",
  },
  {
    section: 4,
    title: "Immigration Court & Case Management",
    description: "Tools for managing active immigration court cases and USCIS case records.",
  },
];

function SidebarLink({ service, onStartService }) {
  const [hover, setHover] = useState(false);
  const disabled = !!service.disabled;
  const wip = !disabled && !!service.inDevelopment;
  const clickable = !disabled && !wip;

  return (
    <li>
      <button
        onClick={() => { if (clickable) onStartService(service.id); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={disabled ? "Temporarily disabled — pending review" : wip ? "In development" : service.description}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: clickable && hover ? brand.porcelainBlue : "none",
          border: "none",
          borderRadius: "6px",
          padding: "6px 10px",
          fontSize: "13px",
          lineHeight: "1.35",
          fontFamily: brand.font,
          color: disabled ? "#9aa0a6" : wip ? brand.constructionYellowText : brand.prussianBlue,
          cursor: clickable ? "pointer" : "not-allowed",
          textDecoration: disabled ? "line-through" : "none",
        }}
      >
        {service.name}
        {wip && (
          <span style={{
            marginLeft: "6px", fontSize: "9px", fontWeight: "700", letterSpacing: "0.05em",
            textTransform: "uppercase", color: "#fff", backgroundColor: brand.constructionYellow,
            padding: "1px 5px", borderRadius: "3px", verticalAlign: "middle",
          }}>
            WIP
          </span>
        )}
      </button>
    </li>
  );
}

function SidebarSection({ title, items, onStartService }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  return (
    <div style={{ marginBottom: "4px" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          width: "100%",
          textAlign: "left",
          background: hover || open ? brand.porcelainBlue : "none",
          border: "none",
          borderRadius: "6px",
          padding: "8px 10px",
          fontSize: "11px",
          fontWeight: "700",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: brand.prussianBlue,
          fontFamily: brand.font,
          cursor: "pointer",
          transition: "background 0.15s ease",
        }}
      >
        <span>{title}</span>
        <span aria-hidden="true" style={{
          fontSize: "10px",
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.15s ease",
          flexShrink: 0,
        }}>
          &#9656;
        </span>
      </button>
      {open && (
        <ul style={{ listStyle: "none", margin: "4px 0 10px", padding: "0 0 0 6px" }}>
          {items.map((service) => (
            <SidebarLink key={service.id} service={service} onStartService={onStartService} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ServiceSidebar({ onStartService }) {
  return (
    <aside style={{
      width: "264px",
      flexShrink: 0,
      position: "sticky",
      top: "16px",
      alignSelf: "flex-start",
      maxHeight: "calc(100vh - 32px)",
      overflowY: "auto",
      backgroundColor: brand.white,
      border: `1px solid ${brand.borderLight}`,
      borderRadius: "12px",
      padding: "18px 14px",
      boxShadow: "0 1px 3px rgba(8,49,77,0.06), 0 1px 2px rgba(8,49,77,0.04)",
    }}>
      <h2 style={{ margin: "0 6px 12px", fontSize: "13px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em", color: brand.prussianBlue, fontFamily: brand.font }}>
        All Services
      </h2>
      {SECTIONS.map(({ section, title }) => {
        const items = services.filter((s) => s.section === section);
        if (items.length === 0) return null;
        return (
          <SidebarSection key={section} title={title} items={items} onStartService={onStartService} />
        );
      })}
    </aside>
  );
}

export function ServiceMenu({ onStartService }) {
  // Sidebar collapses on narrow viewports; inline-style equivalent of a media query.
  const [wide, setWide] = useState(() => window.innerWidth >= 980);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 980);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const divider = (
    <hr style={{
      border: "none",
      borderTop: `1px solid ${brand.borderLight}`,
      margin: "32px 0",
    }} />
  );

  // Cumulative tile count per section so the entrance stagger flows
  // continuously down the page instead of restarting at each section.
  const sectionsWithOffsets = [];
  let tileCount = 0;
  for (const meta of SECTIONS) {
    const items = services.filter((s) => s.section === meta.section);
    sectionsWithOffsets.push({ ...meta, items, startIndex: tileCount });
    tileCount += items.length;
  }

  return (
    <>
      <style>{`
        @keyframes novoCardEnter {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .novo-enter { animation: none !important; }
        }
      `}</style>
      <main style={{ maxWidth: wide ? "1280px" : "960px", margin: "0 auto", padding: "32px 24px", display: "flex", gap: "28px", alignItems: "flex-start" }}>
        {wide && <ServiceSidebar onStartService={onStartService} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: "28px" }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: "18px", fontWeight: "600", color: brand.prussianBlue, fontFamily: brand.font }}>
              Select a Service
            </h2>
            <p style={{ margin: 0, fontSize: "14px", color: brand.textSecondary, fontFamily: brand.font }}>
              Choose the document or task you need to prepare for your client.
            </p>
          </div>

          {sectionsWithOffsets.map(({ section, title, description, items, startIndex }, i) => (
            <React.Fragment key={section}>
              {i > 0 && divider}
              <ServiceSection
                title={title}
                description={description}
                items={items}
                startIndex={startIndex}
                onStartService={onStartService}
              />
            </React.Fragment>
          ))}
        </div>
      </main>

      <footer style={{
        textAlign: "center", padding: "24px", fontSize: "12px", color: brand.textMuted,
        borderTop: `1px solid ${brand.borderLight}`, marginTop: "40px",
        backgroundColor: brand.white, fontFamily: brand.font,
      }}>
        Novo Navigator — Supervised by Novo Legal &nbsp;&middot;&nbsp; Not a substitute for legal advice
      </footer>
    </>
  );
}
